import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workflowApi } from '../services/api';
import {
  WorkflowSummary,
  WorkflowCreate,
  WorkflowUpdate
} from '../types/workflow';
import { toast } from 'sonner';

// Query Keys
export const workflowKeys = {
  all: ['workflows'] as const,
  lists: () => [...workflowKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...workflowKeys.lists(), { filters }] as const,
  details: () => [...workflowKeys.all, 'detail'] as const,
  detail: (id: string) => [...workflowKeys.details(), id] as const,
};

// Get all workflows
export function useWorkflows() {
  return useQuery({
    queryKey: workflowKeys.lists(),
    queryFn: workflowApi.getWorkflows,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get workflow by ID
export function useWorkflow(workflowId: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.detail(workflowId || ''),
    queryFn: () => workflowApi.getWorkflow(workflowId!),
    enabled: !!workflowId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Create workflow mutation
export function useCreateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workflow: WorkflowCreate) => workflowApi.createWorkflow(workflow),
    onSuccess: (newWorkflow) => {
      // Invalidate and refetch workflows list
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      
      // Add the new workflow to the cache
      queryClient.setQueryData(
        workflowKeys.detail(newWorkflow.workflow_id),
        newWorkflow
      );
      
      toast.success('Workflow created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create workflow: ${error.message}`);
    },
  });
}

// Update workflow mutation
export function useUpdateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, updates }: { workflowId: string; updates: WorkflowUpdate }) =>
      workflowApi.updateWorkflow(workflowId, updates),
    onSuccess: (updatedWorkflow) => {
      // Update the workflow in the cache
      queryClient.setQueryData(
        workflowKeys.detail(updatedWorkflow.workflow_id),
        updatedWorkflow
      );
      
      // Invalidate workflows list to refresh summary data
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      
      toast.success('Workflow updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update workflow: ${error.message}`);
    },
  });
}

// Delete workflow mutation
export function useDeleteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workflowId: string) => workflowApi.deleteWorkflow(workflowId),
    onSuccess: (_, workflowId) => {
      // Remove workflow from cache
      queryClient.removeQueries({ queryKey: workflowKeys.detail(workflowId) });
      
      // Update workflows list
      queryClient.setQueryData<WorkflowSummary[]>(
        workflowKeys.lists(),
        (old) => old?.filter(w => w.workflow_id !== workflowId) || []
      );
      
      toast.success('Workflow deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete workflow: ${error.message}`);
    },
  });
}

// Import workflow mutation
export function useImportWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => workflowApi.importWorkflow(file),
    onSuccess: (importedWorkflow) => {
      // Invalidate workflows list
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      
      // Add imported workflow to cache
      queryClient.setQueryData(
        workflowKeys.detail(importedWorkflow.workflow_id),
        importedWorkflow
      );
      
      toast.success('Workflow imported successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to import workflow: ${error.message}`);
    },
  });
}

// Export workflow mutation
export function useExportWorkflow() {
  return useMutation({
    mutationFn: (workflowId: string) => workflowApi.exportWorkflow(workflowId),
    onSuccess: (blob, workflowId) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `workflow-${workflowId}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Workflow exported successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to export workflow: ${error.message}`);
    },
  });
}